import JSZip from "jszip";
import type { Project, WorkspaceFile, WorkspaceScope } from "../types";
import { createProject, db, deleteProject, ensureChat, guessMimeType } from "./db";
import { isKnownTextPath, normalizeProjectPath, slugify } from "./path";

const MAX_ENTRIES = 5_000;
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_TOTAL_BYTES = 200 * 1024 * 1024;

function ownedBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function decodeAsText(path: string, bytes: Uint8Array): string | null {
  if (bytes.includes(0)) return null;
  try {
    const decoded = new TextDecoder("utf-8", { fatal: !isKnownTextPath(path) }).decode(bytes);
    const controls = [...decoded.slice(0, 8_000)].filter((char) => {
      const code = char.charCodeAt(0);
      return code < 9 || (code > 13 && code < 32);
    }).length;
    return controls > 5 ? null : decoded;
  } catch {
    return null;
  }
}

function stripCommonRoot(paths: string[]): string[] {
  const firstSegments = paths.map((path) => path.replace(/\\/g, "/").split("/")[0]);
  const root = firstSegments[0];
  if (root && firstSegments.every((segment) => segment === root) && paths.every((path) => path.includes("/"))) {
    return paths.map((path) => path.replace(/\\/g, "/").split("/").slice(1).join("/"));
  }
  return paths;
}

async function commitImportedProject(name: string, entries: Array<{ path: string; bytes: Uint8Array; mimeType?: string }>): Promise<Project> {
  if (entries.length > MAX_ENTRIES) throw new Error(`Archive trop volumineuse : maximum ${MAX_ENTRIES} fichiers.`);
  const paths = stripCommonRoot(entries.map((entry) => entry.path));
  const seen = new Set<string>();
  let total = 0;
  const now = Date.now();
  const project = await createProject(name, false);
  const chat = await ensureChat(project.id);

  try {
    const records: WorkspaceFile[] = entries.map((entry, index) => {
      const path = normalizeProjectPath(paths[index]);
      const collisionKey = path.toLocaleLowerCase("en-US");
      if (seen.has(collisionKey)) throw new Error(`Collision de chemin dans l’archive : ${path}`);
      seen.add(collisionKey);
      if (entry.bytes.byteLength > MAX_FILE_BYTES) throw new Error(`${path} dépasse la limite de 25 Mio.`);
      total += entry.bytes.byteLength;
      if (total > MAX_TOTAL_BYTES) throw new Error("Le projet décompressé dépasse 200 Mio.");
      const text = decodeAsText(path, entry.bytes);
      return {
        projectId: project.id,
        chatId: chat.id,
        path,
        kind: text === null ? "binary" : "text",
        content: text === null ? ownedBuffer(entry.bytes) : text,
        mimeType: entry.mimeType || guessMimeType(path, text === null ? "application/octet-stream" : "text/plain"),
        size: entry.bytes.byteLength,
        revision: 1,
        createdAt: now,
        updatedAt: now,
      };
    });
    await db.taskFiles.bulkAdd(records);
    return project;
  } catch (error) {
    await deleteProject(project.id);
    throw error;
  }
}

export async function importZip(file: File): Promise<Project> {
  const archive = await JSZip.loadAsync(file, { createFolders: false });
  const files = Object.values(archive.files).filter((entry) => !entry.dir && !entry.name.startsWith("__MACOSX/"));
  if (files.length > MAX_ENTRIES) throw new Error(`Archive trop volumineuse : maximum ${MAX_ENTRIES} fichiers.`);
  const entries: Array<{ path: string; bytes: Uint8Array }> = [];
  let total = 0;
  for (const entry of files) {
    const declaredSize = (entry as unknown as { _data?: { uncompressedSize?: number } })._data?.uncompressedSize;
    if (declaredSize && declaredSize > MAX_FILE_BYTES) throw new Error(`${entry.name} dépasse la limite de 25 Mio.`);
    const bytes = await entry.async("uint8array");
    if (bytes.byteLength > MAX_FILE_BYTES) throw new Error(`${entry.name} dépasse la limite de 25 Mio.`);
    total += bytes.byteLength;
    if (total > MAX_TOTAL_BYTES) throw new Error("Le projet décompressé dépasse 200 Mio.");
    entries.push({ path: entry.name, bytes });
  }
  const name = file.name.replace(/\.zip$/i, "") || "Projet importé";
  return commitImportedProject(name, entries);
}

export async function importFolder(fileList: FileList): Promise<Project> {
  const files = [...fileList];
  if (!files.length) throw new Error("Le dossier est vide.");
  if (files.length > MAX_ENTRIES) throw new Error(`Dossier trop volumineux : maximum ${MAX_ENTRIES} fichiers.`);
  let declaredTotal = 0;
  for (const file of files) {
    if (file.size > MAX_FILE_BYTES) throw new Error(`${file.name} dépasse la limite de 25 Mio.`);
    declaredTotal += file.size;
    if (declaredTotal > MAX_TOTAL_BYTES) throw new Error("Le projet dépasse 200 Mio.");
  }
  const entries: Array<{ path: string; bytes: Uint8Array; mimeType: string }> = [];
  for (const file of files) entries.push({ path: file.webkitRelativePath || file.name, bytes: new Uint8Array(await file.arrayBuffer()), mimeType: file.type });
  const name = (files[0].webkitRelativePath || files[0].name).replace(/\\/g, "/").split("/")[0] || "Projet importé";
  return commitImportedProject(name, entries);
}

export async function exportTaskZip(scope: WorkspaceScope): Promise<void> {
  const [project, chat, files] = await Promise.all([
    db.projects.get(scope.projectId),
    db.chats.get(scope.chatId),
    db.taskFiles.where("chatId").equals(scope.chatId).sortBy("path"),
  ]);
  if (!project || !chat || chat.projectId !== scope.projectId) throw new Error("Tâche introuvable.");
  const zip = new JSZip();
  const rootName = slugify(project.name);
  const root = zip.folder(rootName)!;
  for (const file of files) root.file(file.path, file.content);
  if (!files.length) root.file(".gitkeep", "");
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${rootName}.zip`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
